/**
 * Fog-of-war rendering — D14.
 *
 * Per-tile tri-state overlay per CLIENT_DESIGN §15.3:
 *   - Unrevealed         → opaque black diamond (`alpha = 1`)
 *   - Revealed + in vision → clear (nothing drawn)
 *   - Revealed + out of vision ("memory tile") → half-opaque black
 *                                                 (`alpha = 0.5`)
 *
 * Vision is a Chebyshev radius around the local player (`VISION_RADIUS`
 * tiles; Phase 1 constant, eventually config-driven via
 * `config.client.vision.radius_tiles` alongside the clarity-radius for
 * weather fog). When there is no local player (pre-handshake, or the
 * local player hasn't spawned yet) the layer renders only revealed vs
 * unrevealed — memory treatment is skipped.
 *
 * Bitmap source of truth: `model.fog` is a row-major bit-per-tile
 * `Uint8Array` (hydrated from `SnapshotMessage.fog.bitmap` + updated
 * by the `TileRevealed` event applier). The renderer reads via
 * `fogBitGet`; it never writes.
 *
 * Baseline redraws every frame over the culled visible range — same
 * pattern as `tiles.ts`. When the per-frame cost becomes visible in
 * the D17 perf smoke, swap in an incremental-update texture.
 */

import { Graphics } from 'pixi.js';

import type { WorldModel } from '../world/model';
import { fogBitGet } from '../world/model';
import {
  tileToIso,
  TILE_HEIGHT_PX,
  TILE_WIDTH_PX,
  visibleTileRange,
  type CameraState,
} from './iso';
import type { SceneLayers } from './scene';

/** Chebyshev tile radius of the local player's active vision. */
export const VISION_RADIUS = 8;

/** Alpha for memory tiles (revealed-but-out-of-vision). */
const MEMORY_ALPHA = 0.5;

export interface FogRenderer {
  render: (
    model: WorldModel,
    cam: CameraState,
    selfPlayerId: number | null,
    viewportW: number,
    viewportH: number,
  ) => void;
  destroy: () => void;
}

export function createFogRenderer(layers: SceneLayers): FogRenderer {
  const g = new Graphics();
  layers.fogLayer.addChild(g);

  const halfW = TILE_WIDTH_PX / 2;
  const halfH = TILE_HEIGHT_PX / 2;

  const render = (
    model: WorldModel,
    cam: CameraState,
    selfPlayerId: number | null,
    viewportW: number,
    viewportH: number,
  ): void => {
    g.clear();
    if (model.width === 0 || model.height === 0) return;
    if (model.fog.length === 0) return;

    // Phase 1 stub (carry-forward): until SERVER's `TileRevealed`-on-
    // spawn is wired and the fog-of-war mechanic is genuinely active,
    // an all-zero bitmap means "fog isn't being managed yet," not
    // "every tile is unrevealed." Render nothing in that case so the
    // operator can see the world during J1 / J2. Once the server
    // emits at least one reveal (e.g., the spawn tile on Fresh
    // connect), this guard becomes a no-op and the spec-compliant
    // unrevealed-vs-memory-vs-clear treatment kicks in.
    let anyRevealed = false;
    for (let i = 0; i < model.fog.length; i++) {
      if (model.fog[i] !== 0) {
        anyRevealed = true;
        break;
      }
    }
    if (!anyRevealed) return;

    const r = visibleTileRange(cam, viewportW, viewportH, 2);
    const minX = Math.max(r.minX, 0);
    const minY = Math.max(r.minY, 0);
    const maxX = Math.min(r.maxX, model.width - 1);
    const maxY = Math.min(r.maxY, model.height - 1);

    const self =
      selfPlayerId !== null ? model.players.get(selfPlayerId) : undefined;
    const sx = self ? self.position[0] : Number.POSITIVE_INFINITY;
    const sy = self ? self.position[1] : Number.POSITIVE_INFINITY;

    // Batch opaque diamonds separately from memory-alpha diamonds so we
    // can fill each batch with one `.fill()` call rather than per-tile.
    // PixiJS Graphics v8 retains path state across shapes until fill.
    const memoryPolys: number[][] = [];
    const blackPolys: number[][] = [];

    for (let y = minY; y <= maxY; y++) {
      const rowBase = y * model.width;
      for (let x = minX; x <= maxX; x++) {
        const idx = rowBase + x;
        const revealed = fogBitGet(model.fog, idx);
        const inVision =
          self !== undefined &&
          Math.max(Math.abs(x - sx), Math.abs(y - sy)) <= VISION_RADIUS;

        if (revealed && inVision) continue; // clear

        const iso = tileToIso(x, y);
        const poly = [
          iso.x,
          iso.y - halfH,
          iso.x + halfW,
          iso.y,
          iso.x,
          iso.y + halfH,
          iso.x - halfW,
          iso.y,
        ];
        if (revealed) memoryPolys.push(poly);
        else blackPolys.push(poly);
      }
    }

    // Draw unrevealed (opaque black) first, then memory (semi-transparent).
    if (blackPolys.length > 0) {
      for (const p of blackPolys) g.poly(p);
      g.fill({ color: 0x000000, alpha: 1 });
    }
    if (memoryPolys.length > 0) {
      for (const p of memoryPolys) g.poly(p);
      g.fill({ color: 0x000000, alpha: MEMORY_ALPHA });
    }
  };

  return {
    render,
    destroy: () => {
      g.destroy();
    },
  };
}

/**
 * Classify a tile's fog state — exported for unit testing.
 * `selfPos === null` means no local player is spawned yet; everything
 * falls to the revealed/unrevealed binary (no memory treatment).
 */
export function classifyFog(
  revealed: boolean,
  tileX: number,
  tileY: number,
  selfPos: [number, number] | null,
  visionRadius: number = VISION_RADIUS,
): 'clear' | 'memory' | 'black' {
  if (selfPos === null) return revealed ? 'clear' : 'black';
  const [sx, sy] = selfPos;
  const inVision =
    Math.max(Math.abs(tileX - sx), Math.abs(tileY - sy)) <= visionRadius;
  if (revealed && inVision) return 'clear';
  if (revealed) return 'memory';
  return 'black';
}
